import { useState, useEffect, type ReactNode } from 'react';
import { Lock, AlertCircle } from 'lucide-react';
import { Logo } from '@/components/ui/logo';

const BETA_ACCESS_KEY = 'coi-beta-access';

// The gate password is a build-time value, not a committed secret. The deploy
// workflow injects it for gated environments (dev) from a GitHub secret; prod is
// public and tree-shakes this whole component out, so it never ships there. If a
// gated build has no password configured, the gate fails CLOSED (nothing matches an
// empty string) rather than opening the site.
const BETA_PASSWORD = import.meta.env.VITE_BETA_PASSWORD ?? '';

interface BetaGateProps {
  children: ReactNode;
}

/**
 * Beta Gate - Protects the site during beta testing.
 * Shows a professional password form instead of browser Basic Auth popup.
 * Access is stored in sessionStorage (lasts until browser closes).
 */
export function BetaGate({ children }: BetaGateProps) {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Local dev bypasses the beta gate (no effect on production builds)
  const isDevBypass = import.meta.env.DEV;

  // Prod is public. The deploy workflow stamps the target env into the bundle, so a
  // build that is not explicitly prod (dev, qa, a local `npm run build`) stays gated.
  const isPublicEnv = import.meta.env.VITE_APP_ENVIRONMENT === 'prod';

  // Public portal routes bypass the beta gate entirely
  const isPortalRoute = window.location.pathname.startsWith('/insured/');

  // Check for existing access on mount
  useEffect(() => {
    if (isPortalRoute || isPublicEnv) return;
    const storedAccess = sessionStorage.getItem(BETA_ACCESS_KEY);
    setHasAccess(storedAccess === 'granted');
  }, [isPortalRoute, isPublicEnv]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Simulate a brief delay for UX
    setTimeout(() => {
      // Fail closed: an empty configured password can never be matched.
      if (BETA_PASSWORD && password === BETA_PASSWORD) {
        sessionStorage.setItem(BETA_ACCESS_KEY, 'granted');
        setHasAccess(true);
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
      setIsSubmitting(false);
    }, 300);
  };

  // Local dev, prod, and portal routes skip the gate
  if (isDevBypass || isPublicEnv || isPortalRoute) return <>{children}</>;

  // Still checking access
  if (hasAccess === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-coilive-blue border-t-transparent" />
      </div>
    );
  }

  // Access granted - show the app
  if (hasAccess) {
    return <>{children}</>;
  }

  // No access - show password form
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-coilive-blue-50 via-white to-coilive-gold-50">
      <div className="w-full max-w-md px-6">
        {/* Card */}
        <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
          {/* Logo & Branding */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex justify-center">
              <Logo size="xl" />
            </div>
            <p className="mt-2 text-sm text-gray-500">Beta Access</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Access Password
              </label>
              <div className="relative mt-2">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter beta password"
                  className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-gray-900 placeholder:text-gray-400 focus:border-coilive-blue focus:outline-none focus:ring-2 focus:ring-coilive-blue/20"
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !password}
              className="w-full rounded-lg bg-coilive-blue px-4 py-3 font-semibold text-white transition hover:bg-coilive-blue-700 focus:outline-none focus:ring-2 focus:ring-coilive-blue focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Verifying...' : 'Enter Beta'}
            </button>
          </form>

          {/* Footer */}
          <p className="mt-6 text-center text-xs text-gray-400">
            This site is currently in beta testing.
            <br />
            Contact your administrator for access.
          </p>
        </div>

        {/* External footer */}
        <p className="mt-6 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} COI LIVE. All rights reserved.
        </p>
      </div>
    </div>
  );
}
